"use client"

import { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, ArrowUpDown, Search, ExternalLink, BarChart3 } from "lucide-react"

const TYPE_COLORS = {
  Course: "bg-indigo-500/10 text-indigo-600 border-indigo-500/20",
  Program: "bg-violet-500/10 text-violet-600 border-violet-500/20",
  Bootcamp: "bg-pink-500/10 text-pink-600 border-pink-500/20",
  Academy: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  Certification: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  Workshop: "bg-sky-500/10 text-sky-600 border-sky-500/20",
  Membership: "bg-rose-500/10 text-rose-600 border-rose-500/20",
}
const defaultTypeColor = "bg-muted text-muted-foreground border-border"

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount || 0)

const CompletionBar = ({ value }) => (
  <div className="flex items-center gap-2 min-w-[90px]">
    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${value >= 70 ? "bg-success" : value >= 40 ? "bg-warning" : "bg-destructive"}`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
    <span className="text-sm font-medium text-foreground tabular-nums">{value}%</span>
  </div>
)

const TypeBadge = ({ type }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${TYPE_COLORS?.[type] || defaultTypeColor}`}>
    {type}
  </span>
)

const RowActions = ({ row, onViewProduct, onViewAnalytics }) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted transition-colors"
      >
        Actions
        <ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-md z-20 py-1">
            <button
              disabled={!row?.viewProductPath}
              onClick={() => {
                setOpen(false)
                onViewProduct?.(row)
              }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ExternalLink size={14} /> View product
            </button>
            <button
              onClick={() => {
                setOpen(false)
                onViewAnalytics?.(row)
              }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <BarChart3 size={14} /> View analytics
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const SORT_FIELDS = [
  { key: "name", label: "Product" },
  { key: "learners", label: "Learners" },
  { key: "completion", label: "Completion" },
  { key: "revenue", label: "Revenue" },
]

export function ProductPerformanceTable({ title = "Product Performance", products = [], onViewProduct, onViewAnalytics }) {
    const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("all")
  const [sortField, setSortField] = useState("learners")
  const [sortOrder, setSortOrder] = useState("desc")
  const [expandedIds, setExpandedIds] = useState(new Set())

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const types = useMemo(() => ["all", ...new Set(products?.map((p) => p?.type))], [products])

  const filtered = useMemo(() => {
    let rows = products || []
    if (typeFilter !== "all") rows = rows?.filter((p) => p?.type === typeFilter)
    if (search?.trim()) {
      const q = search.trim().toLowerCase()
      rows = rows?.filter((p) => p?.name?.toLowerCase()?.includes(q))
    }
    return [...rows]?.sort((a, b) => {
      const aVal = a?.[sortField]
      const bVal = b?.[sortField]
      if (typeof aVal === "string") {
        return sortOrder === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal
    })
  }, [products, search, typeFilter, sortField, sortOrder])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortOrder("desc")
    }
  }

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md w-40 sm:w-52 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm bg-background border border-border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {types?.map((t) => (
              <option key={t} value={t}>
                {t === "all" ? "All types" : t}
              </option>
            ))}
          </select>
        </div>
      </div>

      {filtered?.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground bg-muted/30 rounded-lg border border-border">
          No products match your filters yet.
        </div>
      ) : (
        <>
          {/* Desktop / tablet: full table */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground">
                  {SORT_FIELDS?.map((field) => (
                    <th key={field?.key} className="text-left px-4 py-3">
                      <button onClick={() => handleSort(field?.key)} className="flex items-center gap-1 hover:text-foreground transition-colors">
                        {field?.label}
                        <ArrowUpDown size={12} className={sortField === field?.key ? "text-primary" : "opacity-40"} />
                      </button>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="px-4 py-3 w-24" />
                </tr>
              </thead>
              <AnimatePresence mode="wait">
                <motion.tbody
                  key={`${search}-${typeFilter}-${sortField}-${sortOrder}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.15 }}
                >
                  {filtered?.map((row) => (
                    <tr key={row?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground">{row?.name}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground tabular-nums">{row?.learners?.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <CompletionBar value={row?.completion} />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-foreground tabular-nums">{formatCurrency(row?.revenue)}</td>
                      <td className="px-4 py-3">
                        <TypeBadge type={row?.type} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowActions row={row} onViewProduct={onViewProduct} onViewAnalytics={onViewAnalytics} />
                      </td>
                    </tr>
                  ))}
                </motion.tbody>
              </AnimatePresence>
            </table>
          </div>

          {/* Mobile: stacked cards, no horizontal scroll */}
                    <div className="md:hidden space-y-3">
            {filtered?.map((row) => {
              const isExpanded = expandedIds.has(row?.id)
              return (
                <div key={row?.id} className="bg-card border border-border rounded-lg p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(row?.id)}
                    className="w-full flex items-start justify-between gap-2 text-left"
                  >
                    <div>
                      <p className="text-sm font-semibold text-foreground">{row?.name}</p>
                      <div className="mt-1">
                        <TypeBadge type={row?.type} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <div onClick={(e) => e.stopPropagation()}>
                        <RowActions row={row} onViewProduct={onViewProduct} onViewAnalytics={onViewAnalytics} />
                      </div>
                      <ChevronDown
                        size={16}
                        className={`text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                      />
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-3 gap-3 text-xs mt-3 pt-3 border-t border-border">
                          <div>
                            <p className="text-muted-foreground mb-0.5">Learners</p>
                            <p className="font-medium text-foreground tabular-nums">{row?.learners?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-0.5">Completion</p>
                            <CompletionBar value={row?.completion} />
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-0.5">Revenue</p>
                            <p className="font-semibold text-foreground tabular-nums">{formatCurrency(row?.revenue)}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default ProductPerformanceTable